// Material Design 3 Theme for Pfadfinder Wutöschingen
// Forest-inspired color palette for scouts

import { createTheme } from '@mui/material/styles';

// Pfadfinder Scouting Colors - Nature-inspired palette
const pfadfinderColors = {
    // Primary: Forest Green - represents nature, scouting, growth
    primary: {
        main: 'rgb(var(--color-primary))',
        light: 'rgb(var(--color-primary-container))',
        dark: 'rgb(var(--color-primary-on-container))',
        contrastText: 'rgb(var(--color-primary-on))',
    },
    // Secondary: Warm Earth Brown - represents the outdoors, campfires
    secondary: {
        main: 'rgb(var(--color-secondary))',
        light: 'rgb(var(--color-secondary-container))',
        dark: 'rgb(var(--color-secondary-on-container))',
        contrastText: 'rgb(var(--color-secondary-on))',
    },
    // Tertiary: Sky Blue - represents adventure, open skies
    tertiary: {
        main: 'rgb(var(--color-tertiary))',
        light: 'rgb(var(--color-tertiary-container))',
        dark: 'rgb(var(--color-tertiary-on-container))',
        contrastText: 'rgb(var(--color-tertiary-on))',
    },
};

const md3Theme = createTheme({
    palette: {
        mode: 'light',
        primary: pfadfinderColors.primary,
        secondary: pfadfinderColors.secondary,
        error: {
            main: 'rgb(var(--color-error))',
            light: 'rgb(var(--color-error-container))',
            dark: 'rgb(var(--color-error-on-container))',
            contrastText: 'rgb(var(--color-error-on))',
        },
        warning: {
            main: 'rgb(var(--color-tertiary))',
            light: 'rgb(var(--color-tertiary-container))',
            dark: 'rgb(var(--color-tertiary-on-container))',
            contrastText: 'rgb(var(--color-tertiary-on))',
        },
        info: {
            main: 'rgb(var(--color-secondary))',
            light: 'rgb(var(--color-secondary-container))',
            dark: 'rgb(var(--color-secondary-on-container))',
            contrastText: 'rgb(var(--color-secondary-on))',
        },
        success: {
            main: 'rgb(var(--color-primary))',
            light: 'rgb(var(--color-primary-container))',
            dark: 'rgb(var(--color-primary-on-container))',
            contrastText: 'rgb(var(--color-primary-on))',
        },
        background: {
            default: 'rgb(var(--color-surface))',
            paper: 'rgb(var(--color-surface-container))',
        },
        text: {
            primary: 'rgb(var(--color-surface-on))',
            secondary: 'rgb(var(--color-surface-on-variant))',
        },
        divider: 'rgba(var(--color-outline-variant), 0.5)',
    },

    typography: {
        fontFamily: '"Roboto", "Inter", "Helvetica Neue", Arial, sans-serif',
        h1: {
            fontSize: '2.5rem',
            fontWeight: 700,
            lineHeight: 1.2,
            letterSpacing: '-0.02em',
        },
        h2: {
            fontSize: '2rem',
            fontWeight: 600,
            lineHeight: 1.3,
            letterSpacing: '-0.01em',
        },
        h3: {
            fontSize: '1.5rem',
            fontWeight: 600,
            lineHeight: 1.4,
        },
        h4: {
            fontSize: '1.25rem',
            fontWeight: 600,
            lineHeight: 1.4,
        },
        h5: {
            fontSize: '1.125rem',
            fontWeight: 600,
            lineHeight: 1.5,
        },
        h6: {
            fontSize: '1rem',
            fontWeight: 600,
            lineHeight: 1.5,
        },
        body1: {
            fontSize: '1rem',
            lineHeight: 1.6,
        },
        body2: {
            fontSize: '0.875rem',
            lineHeight: 1.5,
        },
        button: {
            textTransform: 'none',
            fontWeight: 600,
            letterSpacing: '0.02em',
        },
        caption: {
            fontSize: '0.75rem',
            lineHeight: 1.4,
        },
    },

    shape: {
        borderRadius: 12,
    },

    shadows: Array(25).fill('none'),

    components: {
        // MUI CSS Baseline for consistent rendering
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    backgroundColor: 'rgb(var(--color-surface))',
                    color: 'rgb(var(--color-surface-on))',
                    scrollbarWidth: 'thin',
                    '&::-webkit-scrollbar': {
                        width: '8px',
                        height: '8px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgb(var(--color-surface-on-variant) / 0.5)',
                        borderRadius: '4px',
                    },
                },
            },
        },

        // Button - MD3 style with rounded corners
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    borderRadius: 100,
                    padding: '10px 24px',
                    fontSize: '0.9375rem',
                    fontWeight: 600,
                    transition: 'all 0.2s ease-in-out',
                },
                contained: {
                    '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: 'none',
                        filter: 'brightness(1.05)',
                    },
                },
                outlined: {
                    borderWidth: 2,
                    '&:hover': {
                        borderWidth: 2,
                    },
                },
                sizeLarge: {
                    padding: '14px 32px',
                    fontSize: '1rem',
                },
                sizeSmall: {
                    padding: '6px 16px',
                    fontSize: '0.8125rem',
                },
            },
        },

        // Card - Clean elevated surface
        MuiCard: {
            defaultProps: {
                elevation: 0,
            },
            styleOverrides: {
                root: {
                    borderRadius: 16,
                    border: '1px solid',
                    borderColor: 'rgb(var(--color-outline-variant))',
                    backgroundColor: 'rgb(var(--color-surface-container-lowest))',
                    color: 'rgb(var(--color-surface-on))',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        backgroundColor: 'rgb(var(--color-surface-container-low))',
                        boxShadow: 'none',
                    },
                    '.dark &': {
                        backgroundColor: 'rgb(var(--color-surface-container)) !important',
                        borderColor: 'rgb(var(--color-outline-variant)) !important',
                        '&:hover': {
                            backgroundColor: 'rgb(var(--color-surface-container-high)) !important',
                        },
                    },
                },
            },
        },

        // Paper
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                    backgroundColor: 'rgb(var(--color-surface-container))',
                    color: 'rgb(var(--color-surface-on))',
                },
                rounded: {
                    borderRadius: 16,
                },
            },
        },

        // TextField - Outlined style by default
        MuiTextField: {
            defaultProps: {
                variant: 'outlined',
                fullWidth: true,
            },
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 12,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: pfadfinderColors.primary.main,
                            },
                        },
                        '&.Mui-focused': {
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderWidth: 2,
                            },
                        },
                    },
                },
            },
        },

        // Input Label
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    fontWeight: 500,
                },
            },
        },

        // AppBar
        MuiAppBar: {
            defaultProps: {
                elevation: 0,
                color: 'inherit',
            },
            styleOverrides: {
                root: {
                    backgroundColor: 'rgb(var(--color-surface))',
                    borderBottom: '1px solid',
                    borderColor: 'rgb(var(--color-outline-variant))',
                    color: 'rgb(var(--color-surface-on))',
                },
            },
        },

        // Drawer
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    borderRadius: 0,
                    border: 'none',
                },
            },
        },

        // Dialog
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: 24,
                    padding: 8,
                },
            },
        },

        // Chip
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    fontWeight: 500,
                },
            },
        },

        // Alert
        MuiAlert: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                },
                standardSuccess: {
                    backgroundColor: 'rgba(var(--color-primary-container), 0.35)',
                    color: 'rgb(var(--color-primary-on-container))',
                },
                standardError: {
                    backgroundColor: 'rgba(var(--color-error-container), 0.35)',
                    color: 'rgb(var(--color-error-on-container))',
                },
                standardWarning: {
                    backgroundColor: 'rgba(var(--color-tertiary-container), 0.35)',
                    color: 'rgb(var(--color-tertiary-on-container))',
                },
                standardInfo: {
                    backgroundColor: 'rgba(var(--color-secondary-container), 0.35)',
                    color: 'rgb(var(--color-secondary-on-container))',
                },
            },
        },

        // Tooltip
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    borderRadius: 8,
                    fontSize: '0.75rem',
                    padding: '8px 12px',
                },
            },
        },

        // IconButton
        MuiIconButton: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    transition: 'all 0.2s ease-in-out',
                },
            },
        },

        // Fab
        MuiFab: {
            styleOverrides: {
                root: {
                    boxShadow: 'none',
                    '&:hover': {
                        boxShadow: 'none',
                        filter: 'brightness(1.05)',
                    },
                },
            },
        },

        // Tab
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.9375rem',
                },
            },
        },

        // List Item Button
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    margin: '2px 8px',
                    '&.Mui-selected': {
                        backgroundColor: 'rgba(var(--color-primary), 0.1)',
                        '&:hover': {
                            backgroundColor: 'rgba(var(--color-primary), 0.15)',
                        },
                    },
                },
            },
        },

        // Divider
        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: 'rgba(var(--color-surface-on), 0.08)',
                },
            },
        },

        // Skeleton
        MuiSkeleton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                },
            },
        },

        // Avatar
        MuiAvatar: {
            styleOverrides: {
                root: {
                    fontWeight: 600,
                },
            },
        },

        // Badge
        MuiBadge: {
            styleOverrides: {
                badge: {
                    fontWeight: 600,
                },
            },
        },
    },
});

export default md3Theme;
